import { Core } from "@faire/design-tokens";
import { redirectAfterSignIn } from "@faire/retailer-visitor-shared/components/AppSignIn/redirectAfterSignIn";
import { AppSignUpForm } from "@faire/retailer-visitor-shared/components/AppSignUp/AppSignUpForm";
import { Disclaimer } from "@faire/retailer-visitor-shared/components/AppSignUp/components/Disclaimer";
import { DrawerSubtitle } from "@faire/retailer-visitor-shared/components/AppSignUp/components/Subtitle";
import { useAppSignUpState } from "@faire/retailer-visitor-shared/components/AppSignUp/useAppSignUpState";
import { SocialSignOnButtons } from "@faire/retailer-visitor-shared/components/SocialSignOnButtons/index";
import { signUpCache } from "@faire/retailer-visitor-shared/features/SignUp/SignUpRequestData";
import { getIsBrandPreview } from "@faire/retailer-visitor-shared/lib/getIsBrandPreview";
import { showSSOButtons } from "@faire/retailer-visitor-shared/lib/ssoUtils";
import { useSSOErrorStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOErrorStore";
import { useSSOMFAStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOMFAStore";
import { useSSOPasswordStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOPasswordStore";
import { useSSOStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOStore";
import { getCurrentPath } from "@faire/web--source/common/Path";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { NonBreaking } from "@faire/web--source/common/string";
import { Column } from "@faire/web--source/slate/Layout";
import { FaireLogo } from "@faire/web--source/slate/logos/FaireLogo";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { Location } from "history";
import React, { useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";

export const AppSignUpDrawer: React.FC = () => {
  const location = useLocation();

  const {
    email,
    modalVariant,
    handleSubmit,
    setEmailValidationErrors,
    markUserEntersEmail,
  } = useAppSignUpState();

  const { userClickedSSOButton } = useSSOStore();

  const { errorMessage } = useSSOErrorStore();

  useEffect(() => {
    // we want a fresh sign up request when user lands on the homepage
    signUpCache.clear();
  }, []);

  const isBrandPreview = getIsBrandPreview();

  const ssoButtonSection = showSSOButtons() ? (
    <SSOButtonsSection isBrandPreview={isBrandPreview} location={location} />
  ) : null;

  return (
    <Column
      align="stretch"
      justify="center"
      style={{
        padding: getSpacing("5x"),
      }}
    >
      <Column justify="center" align="center" flex={1}>
        <FaireLogo size={104} />
        <Spacer height="2x" />
        <Typography variant="displaySSerifRegular" as="h2">
          <StrictLocalMsg
            defaultMessage="Sign up to see more"
            values={{
              nbsp: NonBreaking.space,
            }}
            description={{
              text: "Title of the sign up modal for an experiment",
            }}
          />
        </Typography>
        <Spacer height="1x" />
        <DrawerSubtitle />
        {userClickedSSOButton && errorMessage ? (
          <>
            <Spacer height="2x" />
            <Typography color={Core.text.critical} role="alert" align="center">
              {errorMessage}
            </Typography>
            <Spacer height="3x" />
          </>
        ) : (
          <Spacer height="2x" />
        )}
        <AppSignUpForm
          email={email}
          handleSubmit={handleSubmit}
          setEmailValidationErrors={setEmailValidationErrors}
          markUserEntersEmail={markUserEntersEmail}
          modalVariant={modalVariant}
        />
        {ssoButtonSection}
        <Spacer height="3x" />
      </Column>
      <Disclaimer modalVariant={modalVariant} textAlign="center" />
    </Column>
  );
};

const SSOButtonsSection: React.FC<{
  isBrandPreview: boolean;
  location: Location;
}> = (props) => {
  const isSSR = useIsSSR();
  const location = useLocation();
  const { replace } = useHistory();

  const { setShowConfirmPasswordView } = useSSOPasswordStore();
  const { setMFADescription, setMFAOptions, setMFATitle } = useSSOMFAStore();

  return (
    <SocialSignOnButtons
      disabled={props.isBrandPreview || isSSR}
      onSignIn={(user) => {
        redirectAfterSignIn(location, user);
      }}
      onSelectMFA={(mfaOptions) => {
        setMFAOptions(mfaOptions);
        replace(
          getCurrentPath().delete("signUp").append({ signIn: "1" }).toString()
        );
      }}
      onMFA={(mfaDescription, mfaTitle) => {
        setMFADescription(mfaDescription);
        setMFATitle(mfaTitle);
        replace(
          getCurrentPath().delete("signUp").append({ signIn: "1" }).toString()
        );
      }}
      onConfirmPassword={() => {
        setShowConfirmPasswordView(true);
        replace(
          getCurrentPath().delete("signUp").append({ signIn: "1" }).toString()
        );
      }}
      hideApple
      sourceId="signup_modal"
    />
  );
};
